import type { AskPagePrice } from "./pageVars";

const DURATION_LABEL: Record<AskPagePrice["type"], string> = {
	pass_24h: "24 hours",
	pass_7d: "7 days",
	subscription: "1 month",
};

export const askPageSubscriptionDurationLabel = (
	type: AskPagePrice["type"] | null,
): string | null => (type ? (DURATION_LABEL[type] ?? null) : null);

export const askPageCreatorFirstName = (displayName: string): string => {
	const trimmed = displayName.trim();
	if (!trimmed) {
		return displayName;
	}
	return trimmed.split(/\s+/)[0] ?? trimmed;
};

export const formatAskPageRenewalDay = (iso: string): string =>
	new Intl.DateTimeFormat(undefined, {
		day: "numeric",
		month: "long",
	}).format(new Date(iso));

export const askPageSubscriptionMetaLine = (options: {
	isPaid: boolean;
	entitlementType: AskPagePrice["type"] | null;
	currentPeriodEnd: string | null;
}): string => {
	if (!options.isPaid) {
		return "Free";
	}
	const duration = askPageSubscriptionDurationLabel(options.entitlementType);
	if (!duration) {
		return "Subscribed";
	}
	if (options.currentPeriodEnd) {
		return `${duration} · renews ${formatAskPageRenewalDay(options.currentPeriodEnd)}`;
	}
	return duration;
};

export const askPageUnsubscribeConfirmBody = (options: {
	firstName: string;
	accessUntil: string | null;
	isPaid: boolean;
}): string => {
	if (options.accessUntil) {
		const until = formatAskPageRenewalDay(options.accessUntil);
		return `You can keep asking ${options.firstName} until ${until}. After that you'll need to subscribe again, and you won't be charged.`;
	}
	if (options.isPaid) {
		return `You can keep asking ${options.firstName} until your current billing period ends. After that you'll need to subscribe again, and you won't be charged.`;
	}
	return `You can keep asking ${options.firstName} for free. If you unsubscribe, you'll need to subscribe again to ask new questions.`;
};

export const askPageUnsubscribeIntroBody = (
	displayName: string,
	isPaid: boolean,
): string => {
	const firstName = askPageCreatorFirstName(displayName);
	if (isPaid) {
		return `Your subscription lets you ask questions of everything ${firstName} has made, and follow up on the answers.`;
	}
	return `Your free subscription lets you ask questions of everything ${firstName} has made, and follow up on the answers.`;
};
